/**
 * Direct writer for the shared `sidebar_open` cookie (ThemedOadsSidebar /
 * platform-next, PAD-31716), byte-compatible with what `cookie-state` writes:
 * `encodeURIComponent(JSON.stringify(value))`, 365 days, path=/, SameSite=lax,
 * the shared domain when there is one, Secure on https.
 *
 * Why not `useCookieState` at the app root: its initializer WRITES the default
 * when the cookie is absent, so mounting it in ClientChat would stamp
 * `sidebar_open=true` on `.oppizi.com` from every builder page — login, guest
 * design pages, non-client sessions — that has no sidebar at all. This writes
 * only when the chat actually collapses the menu.
 *
 * Dependency-free ESM so scripts/test-copilot-sidebar-collapse.mjs can run the
 * real source under node:test (`doc`/`now` are injectable for that).
 */
export const SIDEBAR_OPEN_COOKIE = "sidebar_open";
const DAYS = 365;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * @param {boolean} open
 * @param {{ domain?: string, doc?: { cookie: string, location?: { protocol?: string } }, now?: () => number }} [options]
 * @returns {boolean} whether a cookie was written
 */
export const writeSidebarOpenCookie = (open, { domain, doc, now = Date.now } = {}) => {
  const target = doc ?? (typeof document === "undefined" ? undefined : document);
  if (!target) return false;
  const protocol = target.location?.protocol ?? (typeof window === "undefined" ? "" : window.location.protocol);
  const expires = new Date(now() + DAYS * MS_PER_DAY).toUTCString();
  const value = encodeURIComponent(JSON.stringify(Boolean(open)));
  let cookie = `${SIDEBAR_OPEN_COOKIE}=${value}; expires=${expires}; path=/; SameSite=lax`;
  if (domain) cookie += `; domain=${domain}`;
  if (protocol === "https:") cookie += "; Secure";
  target.cookie = cookie;
  return true;
};
